import {assertTokenValidForMutation, handleResponse} from "@/services/service-helpers.ts";
import log from '@/lib/logger';
import {
    CreateMemberDTO,
    Member,
    MemberDeleteBankingDetailsRequest,
    MemberFormOptions,
    MemberListItem,
    MemberService,
    MemberUpdateBankingDetailsRequest,
    MemberUpdateBasicInfoRequest,
    MemberUpdateCommentsRequest,
    MemberUpdateContactDetailsRequest,
    MemberUpdateMembershipRequest
} from "@/models/member.ts";

const NBH_MEMBERS_BASE_URL = `${import.meta.env.VITE_NBH_URL_ROOT}/api/members`
const NBH_MEMBER_OPTIONS_BASE_URL = `${import.meta.env.VITE_NBH_URL_ROOT}/api/options/memberForm`

export async function getMembers(
    token: string | null,
    statusFilter?: string,
    missingConfidentiality?: boolean,
    missingPrivacyConsent?: boolean,
    missingMediaConsent?: boolean
) {
    if (!token) {
        log.error("No token provided to request members");
        return Promise.reject("No token provided");
    }

    // Build query parameters
    const params = new URLSearchParams();
    if (statusFilter && statusFilter !== 'all') {
        params.append('status', statusFilter);
    }
    if (missingConfidentiality === true) {
        params.append('missingConfidentiality', 'true');
    }
    if (missingPrivacyConsent === true) {
        params.append('missingPrivacyConsent', 'true');
    }
    if (missingMediaConsent === true) {
        params.append('missingMediaConsent', 'true');
    }

    const url = params.toString()
        ? `${NBH_MEMBERS_BASE_URL}?${params.toString()}`
        : NBH_MEMBERS_BASE_URL;

    const response = await fetch(url, {
        method: 'GET',
        headers: new Headers({
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        })
    });

    return handleResponse<MemberListItem[]>(response)
}

// Simple version without parameters for member-list-page (like getOrders)
export async function getAllMembers(token: string | null): Promise<MemberListItem[] | void> {
    if (!token) {
        log.error("No token provided to request members");
        return Promise.reject("No token provided");
    }

    const response = await fetch(NBH_MEMBERS_BASE_URL, {
        method: 'GET',
        headers: new Headers({
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        })
    });

    return handleResponse<MemberListItem[]>(response);
}

export async function getMemberFormOptions(token: string) {
    const response = await fetch(NBH_MEMBER_OPTIONS_BASE_URL, {
        method: 'GET',
        headers: new Headers({
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        })
    });

    return handleResponse<MemberFormOptions>(response)
}

export async function createNewMember(token: string, memberData: CreateMemberDTO) {
    assertTokenValidForMutation()
    const response = await fetch(NBH_MEMBERS_BASE_URL, {
        method: "POST",
        headers:
            new Headers({
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            }),
        body: JSON.stringify(memberData)
    })

    return handleResponse<Member>(response)
}

export async function getMemberData(token: string | null, memberId: string | undefined): Promise<Member | void> {
    if (!token) {
        log.error("No token provided for member data retrieval.")
        return Promise.reject("No token provided")
    }
    if (!memberId) {
        log.error("No memberId provided for member data retrieval.")
        return Promise.reject("No memberId provided.")
    }

    const response = await fetch(`${NBH_MEMBERS_BASE_URL}/${memberId}`, {
        method: 'GET',
        headers: new Headers({
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        })
    });

    return handleResponse<Member>(response)
}

export type AddPointsDTO = {
    memberId: string,
    points: number,
    description: string
}

export type CurrentPointsDTO = {
    memberId: string,
    points: number,
    statusFrom: string
}

export async function addPointsToMember(token: string | null, pointsData: AddPointsDTO): Promise<CurrentPointsDTO | void> {
    if (!token) {
        log.error("No token provided while trying to add points to member.")
        return Promise.reject("No token provided")
    }
    assertTokenValidForMutation()

    const response = await fetch(`${NBH_MEMBERS_BASE_URL}/${pointsData.memberId}/points`, {
        method: 'POST',
        headers: new Headers({
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }),
        body: JSON.stringify(pointsData)
    });

    return handleResponse<CurrentPointsDTO>(response)
}

export async function updateBasicMemberInformation(
    token: string | null,
    basicInformationRq: MemberUpdateBasicInfoRequest,
): Promise<Member | void> {
    if (!token) {
        log.error("No token provided while trying to update member information.")
        return Promise.reject("No token provided")
    }

    assertTokenValidForMutation()

    const {memberId, ...basicInformationPayload} = basicInformationRq

    const response = await fetch(`${NBH_MEMBERS_BASE_URL}/${memberId}/basic-details`, {
        method: 'PATCH',
        headers: new Headers({
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }),
        body: JSON.stringify(basicInformationPayload)
    });

    return handleResponse<Member>(response)

}

export async function updateMemberComments(
    token: string | null,
    updateCommentsRq: MemberUpdateCommentsRequest,
): Promise<Member | void> {
    if (!token) {
        log.error("No token provided while trying to update member information.")
        return Promise.reject("No token provided")
    }

    assertTokenValidForMutation()

    const {memberId, ...commentsPayload} = updateCommentsRq

    const response = await fetch(`${NBH_MEMBERS_BASE_URL}/${memberId}/comments`, {
        method: 'PATCH',
        headers: new Headers({
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }),
        body: JSON.stringify(commentsPayload)
    });

    return handleResponse<Member>(response)

}

export async function updateMemberContactDetails(
    token: string | null,
    updateContactDetails: MemberUpdateContactDetailsRequest,
): Promise<Member | void> {
    if (!token) {
        log.error("No token provided while trying to update member information.")
        return Promise.reject("No token provided")
    }

    assertTokenValidForMutation()

    const {memberId, ...commentsPayload} = updateContactDetails

    const response = await fetch(`${NBH_MEMBERS_BASE_URL}/${memberId}/contact-details`, {
        method: 'PATCH',
        headers: new Headers({
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }),
        body: JSON.stringify(commentsPayload)
    });

    return handleResponse<Member>(response)

}

export async function updateMemberMembership(
    token: string | null,
    updateMembershipDetails: MemberUpdateMembershipRequest,
): Promise<Member | void> {
    if (!token) {
        log.error("No token provided while trying to update member information.")
        return Promise.reject("No token provided")
    }

    assertTokenValidForMutation()

    const {memberId, membershipStatusId: _membershipStatusId, ...membershipPayload} = updateMembershipDetails

    const response = await fetch(`${NBH_MEMBERS_BASE_URL}/${memberId}/membership`, {
        method: 'PATCH',
        headers: new Headers({
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }),
        body: JSON.stringify(membershipPayload)
    });

    return handleResponse<Member>(response)

}

export async function updateBankingDetails(
    token: string | null,
    updateBankingDetails: MemberUpdateBankingDetailsRequest
) {
    if (!token) {
        log.error("No token provided while trying to update member information.")
        return Promise.reject("No token provided")
    }

    assertTokenValidForMutation()

    const {memberId, ...bankingDetailsPayload} = updateBankingDetails

    const response = await fetch(`${NBH_MEMBERS_BASE_URL}/${memberId}/banking-details`, {
        method: 'PATCH',
        headers: new Headers({
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }),
        body: JSON.stringify(bankingDetailsPayload)
    });

    return handleResponse<Member>(response)
}

export async function deleteBankingDetails(
    token: string | null,
    bankingDetails: MemberDeleteBankingDetailsRequest
) {
    if (!token) {
        log.error("No token provided while trying to update member information.")
        return Promise.reject("No token provided")
    }

    assertTokenValidForMutation()

    const {memberId, bankAccountId} = bankingDetails

    const response = await fetch(`${NBH_MEMBERS_BASE_URL}/${memberId}/banking-details/${bankAccountId}`, {
        method: 'DELETE',
        headers: new Headers({
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }),
    });

    return handleResponse<Member>(response)
}

export async function fetchAssociatedServicesForMember(
    token: string | null,
    memberId?: string
): Promise<void | MemberService[]> {
    if (!token) {
        log.error("No token provided")
        return Promise.reject("No token provided")
    }

    if (!memberId) {
        log.error("No member ID provided")
        return Promise.reject("No member ID provided")
    }

    const response = await fetch(`${NBH_MEMBERS_BASE_URL}/${memberId}/services`, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    })

    return handleResponse<MemberService[]>(response)
}

export type MemberUpdateServicesRequest = {
    memberId: string,
    serviceIds: string[],
    isDriver: boolean
}

export async function updateMemberServices(
    token: string | null,
    updateServicesRequest: MemberUpdateServicesRequest
): Promise<void> {
    if (!token) {
        log.error("No token provided while trying to update member services.")
        return Promise.reject("No token provided")
    }

    assertTokenValidForMutation()

    const {memberId, serviceIds, isDriver} = updateServicesRequest

    const response = await fetch(`${NBH_MEMBERS_BASE_URL}/${memberId}/services`, {
        method: 'PATCH',
        headers: new Headers({
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }),
        body: JSON.stringify({serviceIds, isDriver})
    });

    return handleResponse<void>(response)
}

export function generateCompleteMemberName(memberData: Member | MemberListItem): string {
    const salutation = memberData.salutation
    let salutationValue
    if (salutation) {
        if (typeof salutation === 'string') {
            salutationValue = salutation || undefined
        } else if (typeof salutation === 'object' && 'value' in salutation) {
            salutationValue = salutation?.value
        }
    }
    return (
        [
            salutationValue,
            memberData.title,
            memberData.firstName,
            memberData.lastName
        ]
            .filter(Boolean)
            .join(' ')
    )
}

// FIXME finish implementation
export async function getMembersWithService(token: string | null, serviceId: string): Promise<MemberListItem[] | void> {
    if (!token) {
        log.error(`No token provided to request members providing service with ID ${serviceId}`);
        return Promise.reject("No token provided");
    }

    try {
        const response = await fetch(
            `${import.meta.env.VITE_NBH_URL_ROOT}/api/members?providesService=${serviceId}`,
            {
                method: 'GET',
                headers: new Headers({
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                })
            }
        );

        return handleResponse<MemberListItem[]>(response);
    } catch (error) {
        log.error(`Error while requesting members providing service #${serviceId}:`, error);
        return Promise.reject(error);
    }
}

export type ResignationPointsTarget = "SOCIAL_ACCOUNT" | "MEMBER" | "LAPSE";

export type CancelMembershipRequest = {
    cancellationDate: string;
    resignationReasonId: string;
    pointsTarget?: ResignationPointsTarget;
    pointsTargetMemberId?: string;
}

export async function cancelMembership(
    token: string | null,
    memberId: string,
    request: CancelMembershipRequest
): Promise<void> {
    if (!token) {
        log.error("No token provided while trying to cancel membership.")
        return Promise.reject("No token provided")
    }
    assertTokenValidForMutation()

    const response = await fetch(`${NBH_MEMBERS_BASE_URL}/${memberId}/cancel-membership`, {
        method: 'POST',
        headers: new Headers({
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }),
        body: JSON.stringify(request)
    });

    return handleResponse<void>(response)
}

export async function revokeMembershipCancellation(
    token: string | null,
    memberId: string
): Promise<void> {
    if (!token) {
        log.error("No token provided while trying to revoke membership cancellation.")
        return Promise.reject("No token provided")
    }
    assertTokenValidForMutation()

    const response = await fetch(`${NBH_MEMBERS_BASE_URL}/${memberId}/revoke-cancellation`, {
        method: 'POST',
        headers: new Headers({
            'Authorization': `Bearer ${token}`,
        }),
    });

    return handleResponse<void>(response)
}

